import { useCurrentUser } from '@/affiliate-dashboard/context/current-user-context';
import { NetworkRole } from '@/affiliate-dashboard/graphql';
import LanguageSwitcher from '@/components/(authenticated)/layout/language-switcher';
import { Logo, LogoVariant } from '@/components/base/logo';
import MenuItem from '@/components/base/menu/menu-item';
import MenuLabel from '@/components/base/menu/menu-label';
import { CredentialsContext } from '@/context/credentials-context';
import { useScreenSize } from '@/hooks/use-screen-size';
import { cn } from '@/utils';
import { useRouterState } from '@tanstack/react-router';
import {
  Code,
  LogOut,
  type LucideIcon,
  UserCog,
  Wrench,
  X,
} from 'lucide-react';
import { useContext, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

interface MenuItem {
  to: string;
  icon: LucideIcon;
  label: string;
}

export interface SidebarProps {
  closeMenu?: () => void;
  items: MenuItem[];
  children?: JSX.Element;
}

export default function Sidebar({
  closeMenu,
  items,
  children,
}: SidebarProps): JSX.Element {
  const [isOpen, _setIsOpen] = useState(true);
  const { isLarge } = useScreenSize();
  const router = useRouterState();
  const { clearToken, setToken, token } = useContext(CredentialsContext);
  const { t } = useTranslation();
  const data = useCurrentUser();
  const [cookie] = useCookies(['ADMIN_TOKEN']);

  function checkMenuItemActive(to: string): boolean {
    if (router.location.pathname === to) {
      return true;
    }
    return false;
  }

  function logout(): void {
    if (cookie.ADMIN_TOKEN && token !== cookie.ADMIN_TOKEN) {
      setToken(String(cookie.ADMIN_TOKEN));
      location.reload();
    } else {
      clearToken();
    }
  }

  function blurItem(to: string): boolean {
    if (to !== '/dashboard/home' && !data.me.emailVerifiedAt) {
      return true;
    }
    return false;
  }
  return (
    <div className="relative h-full w-full">
      <section
        className={cn(
          'flex h-full w-full min-w-[100vw] flex-col divide-y divide-divider rounded-none bg-white pb-3 transition-all duration-300 dark:divide-divider-900 dark:bg-default-gray-950 sm:min-w-[300px] lg:min-w-[250px] lg:rounded-2xl',
          {
            'min-w-0 md:min-w-[75px] md:max-w-[75px] lg:min-w-[75px]': !isOpen,
          },
        )}
      >
        {/* TOP SECTION */}
        <div
          className={cn(
            'flex flex-row flex-wrap items-center justify-center gap-2 overflow-hidden p-5',
            { 'justify-center': isOpen },
          )}
        >
          <div className="mx-auto h-7 w-fit text-black dark:text-white">
            <Logo variant={isOpen ? LogoVariant.Monevis : LogoVariant.Symbol} />
          </div>
          {!isLarge && (
            <div
              className={cn('flex w-fit justify-end', {
                'justify-center': !isOpen,
              })}
            >
              <button
                onClick={closeMenu}
                className="p-2 text-default-gray-900 dark:text-white"
                type="button"
              >
                <X className="h-5 w-5" strokeWidth={2} />
              </button>
            </div>
          )}
        </div>

        {/* MIDDLE MENU ITEMS */}
        <div className="flex h-full flex-col justify-between overflow-y-auto pt-4">
          <div>
            <div
              className={cn(
                'flex w-full flex-col pb-5 transition-all duration-300 sm:max-w-[75px]',
                { 'sm:max-w-[300px] lg:max-w-[250px]': isOpen },
              )}
            >
              {children}
              <div className="flex flex-col gap-[10px]">
                {items.map((item) => (
                  <MenuItem
                    key={item.to}
                    active={checkMenuItemActive(item.to)}
                    icon={item.icon}
                    isOpen={isOpen}
                    to={item.to}
                    onClick={closeMenu}
                    className={cn({
                      'pointer-events-none blur-md': blurItem(item.to),
                    })}
                    tabIndex={blurItem(item.to) ? -1 : 0}
                  >
                    {t(item.label)}
                  </MenuItem>
                ))}
              </div>

              {data?.me.networkRole === NetworkRole.Mlm && (
                <MenuItem
                  active={checkMenuItemActive('/dashboard/upgrade')}
                  icon={UserCog}
                  isOpen={isOpen}
                  to="/dashboard/upgrade"
                  onClick={closeMenu}
                  className={cn({
                    'pointer-events-none blur-md':
                      blurItem('/dashboard/upgrade'),
                  })}
                  tabIndex={blurItem('/dashboard/upgrade') ? -1 : 0}
                >
                  {t('sidebar.upgrade')}
                </MenuItem>
              )}

              {data?.me.role === 'ADMIN' && (
                <>
                  <MenuItem
                    active={checkMenuItemActive('/admin/development')}
                    icon={Code}
                    isOpen={isOpen}
                    to="/dashboard/admin/development"
                    onClick={closeMenu}
                    // eslint-disable-next-line i18next/no-literal-string -- .
                  >
                    Development
                  </MenuItem>

                  <MenuItem
                    active={checkMenuItemActive('/admin')}
                    icon={Wrench}
                    isOpen={isOpen}
                    to="/dashboard/admin"
                    onClick={closeMenu}
                    // eslint-disable-next-line i18next/no-literal-string -- .
                  >
                    Admin
                  </MenuItem>
                </>
              )}
            </div>
          </div>

          {/* BOTTOM MENU ITEMS */}
          <div className="mt-auto flex flex-col gap-1">
            <MenuLabel isOpen={isOpen}>{t('sidebar.changeLanguage')}</MenuLabel>
            <LanguageSwitcher />
            <button
              type="button"
              onClick={() => {
                logout();
              }}
            >
              <MenuItem to="/" icon={LogOut} isOpen={isOpen}>
                {t('sidebar.logOut')}
              </MenuItem>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
